import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { type Component, splitProps } from "solid-js";
import { useI18n } from "@/api/i18n";
import { cn } from "@/libs/cn";
import { getMatchingVariant, type MatchingVariant } from "../utils/matching";

export const matchingProgressBarVariants = cva(
  "h-full rounded-full transition-[width]",
  {
    variants: {
      variant: {
        green: "bg-green-500",
        blue: "bg-blue-500",
        yellow: "bg-amber-400",
        orange: "bg-orange-500",
      },
    },
    defaultVariants: {
      variant: "green",
    },
  },
);

export type MatchingProgressBarProps = VariantProps<
  typeof matchingProgressBarVariants
> & {
  score: number;
  class?: string;
  label?: string;
};

export const MatchingProgressBar: Component<MatchingProgressBarProps> = (
  props,
) => {
  const { t } = useI18n();
  const [local, rest] = splitProps(props, [
    "score",
    "class",
    "variant",
    "label",
  ]);

  const variant = (): MatchingVariant =>
    local.variant ?? getMatchingVariant(local.score);
  const percent = () => Math.min(100, Math.max(0, local.score));
  const labelText = () =>
    local.label ?? t("company.search.freelancers.result.confidence_score");

  return (
    <div class={cn("flex items-center gap-3 w-full", local.class)} {...rest}>
      <span class="text-sm text-gray-600 shrink-0">{labelText()}</span>
      <div class="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
        <div
          class={matchingProgressBarVariants({ variant: variant() })}
          style={{ width: `${percent()}%` }}
        />
      </div>
      <span class="text-sm font-medium text-gray-700 shrink-0 tabular-nums">
        {Math.round(percent())}%
      </span>
    </div>
  );
};
